import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { endpoints } from '@/api/reports';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { createNameFieldSchema } from '@/util/validation/schema-builders';

type Props = {
  title?: string;
  defaultName?: string;
  excludeName?: string;
  submitLabel: string;
  isSubmitting?: boolean;
  onSubmit: (name: string) => void;
  onCancel: () => void;
};

export const ReportNameForm = ({
  title,
  defaultName = '',
  excludeName,
  submitLabel,
  isSubmitting = false,
  onSubmit,
  onCancel,
}: Props) => {
  const { t } = useTranslation();

  const schema = yup.object({
    name: createNameFieldSchema({ getEndpoint: endpoints.checkName, excludeName }),
  });

  type FormValues = yup.InferType<typeof schema>;
  const form = useForm<FormValues>({
    resolver: yupResolver<FormValues, unknown, FormValues>(schema),
    mode: 'onSubmit',
    defaultValues: { name: defaultName },
  });

  const isBusy = isSubmitting || form.formState.isSubmitting;

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-4"
        onSubmit={form.handleSubmit((data) => onSubmit((data.name ?? '').trim()))}
      >
        {title && <h3 className="text-base leading-6 font-medium text-foreground">{title}</h3>}
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('analytics.reports_name_label')}</FormLabel>
              <FormControl>
                <Input autoFocus placeholder={t('analytics.reports_name_placeholder')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost-neutral" size="sm" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" size="sm" isLoading={isBusy}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
};
